import { changed, noOp, rejected, type Command, type CommandExecution } from './commands';
import { damage, type DamageSet } from './damage';
import type { Matrix } from './geometry';
import { findLayer, walkLayers, type EditorDocument, type Layer, type LayerId } from './model';
import type { RasterTileStore } from './tiles';
import type { EditableText } from '../vector/types';

/**
 * Template slots mark layers whose content is meant to change between uses of
 * a reusable document (event flyers, recurring covers). The marker lives in
 * `layer.extensions`, so it round-trips through `.maipo` files without any
 * schema change and is invisible to readers that do not know the key.
 */
export const SLOT_EXTENSION_KEY = 'maipo:slot';
const LEGACY_SLOT_KEY = 'vireo:slot';

export type SlotRole = 'text' | 'image';

export interface SlotInfo {
  name: string;
  role: SlotRole;
  /** Saved generation prompt for image slots; travels with the template. */
  prompt?: string;
}

/** The slot role a layer can carry, or null for kinds that cannot be slots. */
export function slotRoleForLayer(layer: Layer): SlotRole | null {
  if (layer.kind === 'text') return 'text';
  if (layer.kind === 'raster') return 'image';
  return null;
}

/** Validated slot marker for a layer, or null when absent or malformed. */
export function layerSlot(layer: Layer): SlotInfo | null {
  const value = layer.extensions[SLOT_EXTENSION_KEY] ?? layer.extensions['vireo:slot'];
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.name !== 'string' || record.name.trim().length === 0) return null;
  if (record.role !== 'text' && record.role !== 'image') return null;
  const slot: SlotInfo = { name: record.name, role: record.role };
  if (typeof record.prompt === 'string') slot.prompt = record.prompt;
  return slot;
}

export interface SlotEntry {
  layer: Layer;
  slot: SlotInfo;
}

/** All slot-marked layers in layer-tree order. */
export function listSlots(document: EditorDocument): SlotEntry[] {
  const entries: SlotEntry[] = [];
  for (const layer of walkLayers(document.root)) {
    const slot = layerSlot(layer);
    if (slot) entries.push({ layer, slot });
  }
  return entries;
}

export class SetLayerSlotCommand implements Command {
  readonly label: string;
  private before?: { value: unknown; legacy: unknown };

  constructor(
    private readonly layerId: LayerId,
    private readonly slot: SlotInfo | null,
  ) {
    this.label = slot ? `Mark slot “${slot.name}”` : 'Remove slot marker';
  }

  retainedBytes(): number {
    return 256 + JSON.stringify(this.slot ?? null).length * 2 + JSON.stringify(this.before?.value ?? null).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer) return rejected('Layer no longer exists.');
    const next = this.slot;
    if (next) {
      if (slotRoleForLayer(layer) !== next.role) return rejected('Slot role does not match the layer kind.');
      const duplicate = walkLayers(document.root).some((candidate) => candidate.id !== layer.id && layerSlot(candidate)?.name === next.name);
      if (duplicate) return rejected(`A slot named “${next.name}” already exists.`);
    }
    const current = layer.extensions[SLOT_EXTENSION_KEY];
    const legacy = layer.extensions[LEGACY_SLOT_KEY];
    if (legacy === undefined && JSON.stringify(current ?? null) === JSON.stringify(next)) return noOp();
    this.before ??= {
      value: current === undefined ? undefined : structuredClone(current),
      legacy: legacy === undefined ? undefined : structuredClone(legacy),
    };
    // The legacy vireo:slot marker is superseded by the maipo:slot key (or by
    // its removal) — drop it so an unmarked slot can't resurface from it.
    delete layer.extensions[LEGACY_SLOT_KEY];
    if (next) layer.extensions[SLOT_EXTENSION_KEY] = structuredClone(next);
    else delete layer.extensions[SLOT_EXTENSION_KEY];
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || !this.before) return;
    if (this.before.value === undefined) delete layer.extensions[SLOT_EXTENSION_KEY];
    else layer.extensions[SLOT_EXTENSION_KEY] = structuredClone(this.before.value);
    if (this.before.legacy === undefined) delete layer.extensions[LEGACY_SLOT_KEY];
    else layer.extensions[LEGACY_SLOT_KEY] = structuredClone(this.before.legacy);
  }

  damage(): DamageSet {
    return damage({ layerIds: [this.layerId] });
  }
}

/**
 * Fills a text slot as ONE undoable step: sets the value, optionally grows the
 * layout box for longer text, and optionally shifts the layer so right/center
 * aligned text keeps its visual anchor. Growth and shift are precomputed by
 * the caller (text measurement needs the DOM); the command applies them
 * deterministically so undo/redo restore text, box, and transform together.
 */
export class FillTextCommand implements Command {
  readonly label = 'Edit text';
  private before?: { text: EditableText; transform: Matrix };
  private after?: { text: EditableText; transform: Matrix };

  constructor(
    private readonly layerId: LayerId,
    private readonly value: string,
    private readonly boxGrowth = 0,
    private readonly anchorShift = 0,
  ) {}

  retainedBytes(): number {
    return 256 + this.value.length * 4 + (this.before ? JSON.stringify(this.before.text).length * 2 : 0);
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'text') return rejected('Text layer no longer exists.');
    if (layer.text.schemaVersion === 2) return rejected('Portable text edits require asynchronous layout preparation.');
    if (layer.locked) return rejected('Text layer is locked.');
    if (this.after) {
      layer.text = structuredClone(this.after.text);
      layer.transform = this.after.transform;
      return changed(this.damage());
    }
    if (layer.text.value === this.value && this.boxGrowth === 0) return noOp();
    this.before ??= { text: structuredClone(layer.text), transform: layer.transform };
    layer.text = {
      ...structuredClone(layer.text),
      value: this.value,
      box: { ...layer.text.box, width: layer.text.box.width + this.boxGrowth },
    };
    if (this.anchorShift !== 0) {
      const transform = layer.transform;
      layer.transform = [transform[0], transform[1], transform[2], transform[3], transform[4] - this.anchorShift, transform[5]];
    }
    this.after = { text: structuredClone(layer.text), transform: layer.transform };
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'text' || !this.before) return;
    layer.text = structuredClone(this.before.text);
    layer.transform = this.before.transform;
  }

  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'text' || !this.after) return;
    layer.text = structuredClone(this.after.text);
    layer.transform = this.after.transform;
  }

  damage(): DamageSet {
    return damage({ layerIds: [this.layerId] });
  }
}

/**
 * Swaps a raster layer's entire tile store. Undo restores the previous store
 * object; the renderer keys tile surfaces by store identity, so no stale
 * surfaces survive the swap. The replacement store may have different
 * dimensions from the original.
 */
export class ReplaceRasterCommand implements Command {
  readonly label: string;
  private before?: RasterTileStore;

  constructor(
    private readonly layerId: LayerId,
    private readonly next: RasterTileStore,
    label = 'Replace image',
  ) {
    this.label = label;
  }

  retainedBytes(): number {
    return 256 + this.next.byteLength() + (this.before?.byteLength() ?? 0);
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'raster') return rejected('Raster layer no longer exists.');
    if (layer.locked || layer.pixelsLocked) return rejected('Layer pixels are locked.');
    if (layer.raster === this.next) return noOp();
    this.before ??= layer.raster;
    layer.raster = this.next;
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster' && this.before) layer.raster = this.before;
  }

  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster') layer.raster = this.next;
  }

  damage(): DamageSet {
    return damage({ layerIds: [this.layerId], structural: true });
  }
}
